import { c as _c } from "react/compiler-runtime";
import { ErrorBoundary } from "@/components/error-boundary";
import { useSearchPageInteractionStore } from "@/hooks/search";
import { useOptimizedClicked } from "@/hooks/search/use-optimized-clicked";
import { usePropertyInteractionStore } from "@/stores/property-interaction-store";
import type { MapItemSUP, MapItemWithCoodId } from "@/types/search";
import { logger } from "@/utils/logger";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import { Eye } from "lucide-react";
import React from "react";
import { SafePin } from "./safe-pin";
const MarkerFallback = t0 => {
  const $ = _c(2);
  const {
    children
  } = t0;
  let t1;
  if ($[0] !== children) {
    t1 = <div className="w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs relative"><div className="absolute top-full left-0 z-[-1]">{children}</div></div>;
    $[0] = children;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};

// Helper for deep comparison of sups array
function areSupsEqual(sups1?: MapItemSUP[], sups2?: MapItemSUP[]) {
  if (sups1 === sups2) return true;
  if (!sups1 || !sups2) return false;
  if (sups1.length !== sups2.length) return false;

  // Compare IDs to ensure content hasn't changed
  for (let i = 0; i < sups1.length; i++) {
    if (sups1[i]._id !== sups2[i]._id) return false;
  }
  return true;
}

// Memoized individual marker component to prevent unnecessary re-renders
export const Marker = React.memo(function MarkerComponent(t0) {
  const $ = _c(48);
  const {
    marker
  } = t0;
  let t1;
  if ($[0] !== marker) {
    bb0: {
      if (!marker || !marker._id) {
        t1 = null;
        break bb0;
      }
      let coords;
      if (typeof marker._id === "string") {
        ;
        try {
          coords = JSON.parse(`[${marker._id}]`);
        } catch (t2) {
          const err = t2;
          logger.error("Failed to parse marker coordinates:", err);
          t1 = null;
          break bb0;
        }
      } else {
        if (Array.isArray(marker._id)) {
          coords = marker._id as [number, number];
        } else {
          logger.error("Invalid marker _id format:", marker._id);
          t1 = null;
          break bb0;
        }
      }
      if (!Array.isArray(coords) || coords.length !== 2 || isNaN(coords[0]) || isNaN(coords[1])) {
        logger.error("Invalid coordinates:" + JSON.stringify(coords), coords);
        t1 = null;
        break bb0;
      }
      t1 = coords;
    }
    $[0] = marker;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const coordinates = t1;
  let t2;
  if ($[2] !== coordinates) {
    t2 = coordinates?.toString() || "";
    $[2] = coordinates;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const coordinatesString = t2;
  const markerRef = React.useRef(null);
  const safePinRef = React.useRef(null);
  const isHoveredRef = React.useRef(false);
  const isGloballyHoveredRef = React.useRef(false);
  const {
    isClicked,
    handleToggleClick
  } = useOptimizedClicked(coordinatesString);
  const store = useSearchPageInteractionStore();
  const openPropertyOverlay = usePropertyInteractionStore(_temp);
  let t3;
  if ($[4] !== marker.sups) {
    t3 = state_0 => {
      if (!marker.sups || !state_0.viewedProperties) {
        return 0;
      }
      let count = 0;
      for (const prop of marker.sups) {
        if (state_0.viewedProperties.has(prop._id)) {
          count++;
        }
      }
      return count;
    };
    $[4] = marker.sups;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const viewCount = usePropertyInteractionStore(t3);
  const count_0 = marker.sups ? marker.sups?.length || 0 : 0;
  const isViewed = viewCount > 0;
  const allViewed = viewCount === count_0;
  let t4;
  if ($[6] !== isClicked || $[7] !== isViewed) {
    t4 = isHovered => {
      if (!safePinRef.current) {
        return;
      }
      const element = safePinRef.current;
      element.classList.remove("scale-150", "!bg-secondary", "text-black", "shadow-lg", "!bg-accent", "text-accent-foreground");
      if (isHovered || isClicked) {
        element.classList.add("scale-150", "!bg-secondary", "text-black", "shadow-lg");
      }
      if (isViewed && !isHovered && !isClicked) {
        element.classList.add("!bg-accent", "text-accent-foreground");
      }
      if (markerRef.current) {
        const zIndex = isHovered || isGloballyHoveredRef.current || isClicked ? 9999 : 1;
        markerRef.current.zIndex = zIndex;
      }
    };
    $[6] = isClicked;
    $[7] = isViewed;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const applyMarkerEffects = t4;
  let t5;
  let t6;
  if ($[9] !== applyMarkerEffects) {
    t5 = () => {
      applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
    };
    t6 = [applyMarkerEffects];
    $[9] = applyMarkerEffects;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  React.useEffect(t5, t6);
  let t7;
  if ($[12] !== applyMarkerEffects || $[13] !== coordinatesString || $[14] !== store) {
    t7 = () => {
      isHoveredRef.current = true;
      applyMarkerEffects(true);
      store.getState().setHoveredMarkerId(coordinatesString);
    };
    $[12] = applyMarkerEffects;
    $[13] = coordinatesString;
    $[14] = store;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleMouseEnter = t7;
  let t8;
  if ($[16] !== applyMarkerEffects || $[17] !== store) {
    t8 = () => {
      isHoveredRef.current = false;
      applyMarkerEffects(false);
      store.getState().setHoveredMarkerId(null);
    };
    $[16] = applyMarkerEffects;
    $[17] = store;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleMouseLeave = t8;
  let t10;
  let t9;
  if ($[19] !== applyMarkerEffects || $[20] !== coordinatesString || $[21] !== store) {
    t9 = () => {
      const unsubscribe = store.subscribe(_temp2, hoveredId => {
        const wasGloballyHovered = isGloballyHoveredRef.current;
        isGloballyHoveredRef.current = hoveredId === coordinatesString;
        if (wasGloballyHovered !== isGloballyHoveredRef.current) {
          applyMarkerEffects(isHoveredRef.current || isGloballyHoveredRef.current);
        }
      });
      return unsubscribe;
    };
    t10 = [coordinatesString, applyMarkerEffects, store];
    $[19] = applyMarkerEffects;
    $[20] = coordinatesString;
    $[21] = store;
    $[22] = t10;
    $[23] = t9;
  } else {
    t10 = $[22];
    t9 = $[23];
  }
  React.useEffect(t9, t10);
  let t11;
  if ($[24] !== coordinates || $[25] !== handleToggleClick || $[26] !== marker || $[27] !== openPropertyOverlay) {
    t11 = () => {
      if (!marker || !coordinates) {
        return;
      }
      if (marker.sups && marker.sups?.length > 0) {
        const supIds = marker.sups.map(_temp3);
        const initialSupId = supIds[0];
        openPropertyOverlay({
          sups: marker.sups,
          initialSupId,
          projectName: marker.projectName || ""
        });
      }
      handleToggleClick({
        coordinates,
        projectId: marker.projectId,
        projectName: marker.projectName,
        sups: marker.sups
      });
    };
    $[24] = coordinates;
    $[25] = handleToggleClick;
    $[26] = marker;
    $[27] = openPropertyOverlay;
    $[28] = t11;
  } else {
    t11 = $[28];
  }
  const onClick = t11;
  if (!coordinates) {
    return null;
  }
  const t12 = `marker-${coordinatesString}`;
  let t13;
  if ($[29] !== coordinates[0] || $[30] !== coordinates[1]) {
    t13 = {
      lat: coordinates[1],
      lng: coordinates[0]
    };
    $[29] = coordinates[0];
    $[30] = coordinates[1];
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const t14 = isClicked ? 9999 : 1;
  const t15 = isViewed ? "!bg-accent" : "";
  let t16;
  if ($[32] !== allViewed || $[33] !== count_0 || $[34] !== isViewed || $[35] !== viewCount) {
    t16 = isViewed ? <div className="flex items-center justify-center">{allViewed ? <Eye className="w-3 h-3 text-white" /> : <span className="text-[0.6rem] font-semibold text-white">{viewCount}/{count_0}</span>}</div> : <div className="text-white font-semibold text-xs">{count_0}</div>;
    $[32] = allViewed;
    $[33] = count_0;
    $[34] = isViewed;
    $[35] = viewCount;
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== t15 || $[38] !== t16) {
    t17 = <div className="relative"><SafePin ref={safePinRef} className={t15}>{t16}</SafePin></div>;
    $[37] = t15;
    $[38] = t16;
    $[39] = t17;
  } else {
    t17 = $[39];
  }
  let t18;
  if ($[40] !== handleMouseEnter || $[41] !== handleMouseLeave || $[42] !== onClick || $[43] !== t12 || $[44] !== t13 || $[45] !== t14 || $[46] !== t17) {
    t18 = <ErrorBoundary fallback={MarkerFallback}><AdvancedMarker key={t12} position={t13} onClick={onClick} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} zIndex={t14} ref={markerRef}>{t17}</AdvancedMarker></ErrorBoundary>;
    $[40] = handleMouseEnter;
    $[41] = handleMouseLeave;
    $[42] = onClick;
    $[43] = t12;
    $[44] = t13;
    $[45] = t14;
    $[46] = t17;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  return t18;
}, (prevProps, nextProps) => {
  // Custom comparison to prevent unnecessary re-renders
  const m1 = prevProps.marker;
  const m2 = nextProps.marker;

  // Deep compare coordinates (values instead of array reference)
  // marker._id is strictly number[] from store types
  const coordsEqual = m1._id === m2._id ||
  // Reference check fallback
  Array.isArray(m1._id) && Array.isArray(m2._id) && m1._id.length === 2 && m2._id.length === 2 && m1._id[0] === m2._id[0] && m1._id[1] === m2._id[1];
  if (!coordsEqual) return false;

  // Compare projectId
  if (m1.projectId !== m2.projectId) return false;

  // Deep compare sups IDs to ensure content hasn't changed
  return areSupsEqual(m1.sups, m2.sups);
});
function _temp(state) {
  return state.openPropertyOverlay;
}
function _temp2(state_1) {
  return state_1.hoveredMarkerId;
}
function _temp3(sup) {
  return sup._id;
}